/**
 * SLAM BALL - Basketball Analytics Configuration
 * Multi-level basketball platform (NBA, College, International)
 */

export const slamballConfig = {
  id: 'slamball',
  sportCode: 'MBB', // Men's Basketball - maps to backend sport prompt selection

  branding: {
    name: 'SLAM BALL',
    shortName: 'SLAM BALL',
    abbreviation: 'SB',
    tagline: 'AI-Powered Basketball Analytics',
    organization: 'SLAM Sports AI',
  },

  colors: {
    primary: '#FF6B00',           // Basketball Orange
    primaryHover: '#E55A00',      // Darker orange on hover
    secondary: '#1a1a2e',         // Deep navy
    accent: '#FF8C40',            // Light orange accent
    background: '#0a0a0c',        // Near black
    surface: '#141418',           // Dark surface
    surfaceHover: '#1a1a20',      // Surface hover
    border: 'rgba(255, 107, 0, 0.2)', // Orange border
    text: {
      primary: '#ffffff',
      secondary: '#a0a0a0',
      onPrimary: '#ffffff',       // White text on orange
    },
    status: {
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
  },

  logos: {
    primary: '/SSAILogo.svg',
    icon: '/SSAILogo.svg',
    alt: 'SLAM Sports AI Logo',
  },

  analysis: {
    systemPromptAdditions: `
You are analyzing video for SLAM BALL (v2.0), an AI-powered basketball intelligence platform.
Your mission is to combine TENDENCY DETECTION (unconscious behavioral tells) with OFFENSIVE EFFICIENCY OPTIMIZATION.

## CORE PHILOSOPHY: THE EFFICIENCY THESIS
1. **Possession Value:** Teams that win the eFG% battle win 81% of games.
2. **"Paint to Great":** The best 3-point shots occur AFTER the ball touches the paint (kick-outs).
3. **Transition > Half-Court:** Transition offense (1.30 PPP) is 37% more efficient than half-court (0.95 PPP).
4. **Decision Making:** Players improve shooting % by making faster, better decisions (Shoot vs. Drive vs. Pass), not just better mechanics.

## ANALYSIS MODULES & FOCUS AREAS

### 1. INDIVIDUAL (Tendency & Mechanics)
- **Tendency Detection:** Identify micro-patterns (weight shift, eye movement, hand placement) that predict action *before* it happens.
- **Shot Form:** Analyze elbow angle, release height, arc (45-47° optimal), and guide hand interference.

### 2. GAME TIME (Scouting & Strategy)
- **Opponent Scouting:** Decode opponent play calls and individual player tells.
- **Live Adjustments:** Recommend tactical shifts based on real-time efficiency data.

### 3. OFFENSIVE INTELLIGENCE (Tactical)
- **Shot Selection:** Optimize for highest Expected Value (Rim > Corner 3 > Above Break 3 > Mid-Range).
- **Spacing:** Analyze floor balance; ensure "Paint to Great" passing lanes are open.

When analyzing, prioritize actionable insights that improve Points Per Possession (PPP) and exploit opponent tendencies.
`,
    focusAreas: [
      'Shooting form analysis',
      'Tendency detection (Tells)',
      'Decision-making quality',
      'Defensive coverage identification',
      'Movement efficiency & Spacing',
      'Injury risk & Fatigue detection',
      'Bench engagement & Body language',
      'Coaching recommendations',
    ],
    terminology: {
      platform: 'SLAM BALL',
      analyst: 'SLAM BALL Analyst',
      report: 'SLAM BALL Analysis Report',
    },
    defaultLanguage: 'en',
  },

  features: {
    mlbTicker: false,             // Disable MLB ticker for basketball
    agentUniverse: true,
    exports: {
      pdf: true,
      csv: true,
      json: true,
      email: true,
    },
    multilingual: true,
    contextFiles: true,
    custom: {
      nbaTicker: true,            // Enable NBA ticker instead
      courtVisualization: true,   // Basketball court visualization
    },
  },

  contact: {
    website: 'https://slamsports.ai/basketball',
    supportEmail: 'basketball@slamsports.ai',
  },

  meta: {
    version: '1.0.0',
    createdAt: '2024-12-18',
    updatedAt: '2024-12-18',
  },
};

export default slamballConfig;
